import { useEffect, useState } from "react";
import { Divider, Modal } from "rsuite";
import { gatewayCRUD } from "../../../actions/gateway";
import { createPortal } from "react-dom";
import moment from "moment";
import { char, code, number } from "../../../utils";
import Confirmation from "../../../utils/confirmation";
import { selector } from "../../../api";
import { useNotification } from "../../../utils/notification";

export const ClearanceDetails = (props) => {
  const { open, setOpen, index, setIndex, dataId } = props;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const openNotification = useNotification();
  const user = selector.User();

  useEffect(() => {
    if (dataId) {
      setLoading(true);
      setTimeout(() => {
        gatewayCRUD
          .loadInvoiceClearanceById(dataId)
          .then((resp) => {
            setData(resp.data);
          })
          .catch(() => {
            setData(null);
          })
          .finally(() => {
            setLoading(false);
          });
      }, 500);
    }
  }, [dataId, index]);

  const handleApprove = () => {
    if (dataId) {
      setLoading(true);
      setTimeout(() => {
        gatewayCRUD
          .approveInvoiceClearance(dataId, {
            approved_by: user?.id,
            comment: comment,
          })
          .then(() => {
            openNotification(
              "success",
              "Success",
              "Thank you....! invoice clearance has been approved successfully.",
            );
            setOpen(false);
            setIndex((index) => index + 1);
            setShowApproveModal(false);
          })
          .catch((error) => {
            const responseData = error?.response?.data;
            let errorMessage = "Something went wrong. Please try again.";

            if (typeof responseData === "string") errorMessage = responseData;
            else if (responseData?.error) errorMessage = responseData.error;
            else if (responseData?.detail) errorMessage = responseData.detail;
            else if (responseData?.message) errorMessage = responseData.message;
            else if (typeof responseData === "object") {
              const firstKey = Object.keys(responseData)[0];
              const firstError = responseData[firstKey];
              if (Array.isArray(firstError)) errorMessage = firstError[0];
            }
            openNotification("error", "Error", errorMessage);
          })
          .finally(() => {
            setLoading(false);
          });
      }, 500);
    }
  };

  const handleReject = () => {
    if (dataId) {
      setLoading(true);
      setTimeout(() => {
        gatewayCRUD
          .rejectInvoiceClearance(dataId, {
            rejected_by: user?.id,
            comment: comment,
          })
          .then(() => {
            openNotification(
              "success",
              "Success",
              "Thank you....! invoice clearance has been rejected successfully.",
            );
            setOpen(false);
            setIndex((index) => index + 1);
            setShowRejectModal(false);
          })
          .catch((error) => {
            const responseData = error?.response?.data;
            let errorMessage = "Something went wrong. Please try again.";

            if (typeof responseData === "string") errorMessage = responseData;
            else if (responseData?.error) errorMessage = responseData.error;
            else if (responseData?.detail) errorMessage = responseData.detail;
            else if (responseData?.message) errorMessage = responseData.message;
            else if (typeof responseData === "object") {
              const firstKey = Object.keys(responseData)[0];
              const firstError = responseData[firstKey];
              if (Array.isArray(firstError)) errorMessage = firstError[0];
            }
            openNotification("error", "Error", errorMessage);
          })
          .finally(() => {
            setLoading(false);
          });
      }, 500);
    }
  };

  const handleConfirm = (e) => {
    Confirmation(
      `Dear ${char.toTitleCase(code.getFirstWord(user?.first_name))},`,
      `Do you want to ${e === "1" ? "reject" : "approve"} this clearance...?`,
      e === "1" ? handleConfirmReject : handleConfirmApprove,
      null,
    );
  };

  const handleConfirmApprove = () => {
    setComment("");
    setError("");
    setShowApproveModal(true);
  };

  const handleConfirmReject = () => {
    setComment("");
    setError("");
    setShowRejectModal(true);
  };

  const handleSubmitApprove = async () => {
    if (!comment.trim()) {
      setError("Comment is required");
      return;
    }

    try {
      setLoading(true);
      handleApprove();
    } catch (err) {
      console.error(err);
      setLoading(false);
      setShowApproveModal(false);
    }
  };

  const handleSubmitReject = async () => {
    if (!comment.trim()) {
      setError("Comment is required");
      return;
    }

    try {
      setLoading(true);
      handleReject();
    } catch (err) {
      console.error(err);
      setLoading(false);
      setShowRejectModal(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setData(null);
          setComment("");
          setError("");
        }}
        size="md"
        backdrop="static"
        enforceFocus={false}
      >
        <Modal.Header>
          <Modal.Title>Clearance Details</Modal.Title>
        </Modal.Header>
        <Divider />
        <Modal.Body>
          <div className="container-fluid position-relative">
            {loading && (
              <div
                className="position-absolute w-100 h-100 top-0 start-0 d-flex justify-content-center align-items-center"
                style={{
                  background: "rgba(255, 255, 255, 0.7)",
                  zIndex: 9999,
                }}
              >
                <div
                  className="spinner-border text-primary"
                  role="status"
                  style={{ width: 40, height: 40 }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            <div className="row">
              <div className="col-12">
                <div className="card shadow-sm">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                      Payment Details - Ref: {char.toUpperCase(data?.reference)}
                    </h5>
                  </div>
                  <div className="card-body">
                    {/* Row 1 */}
                    <div className="row mb-3">
                      <div className="col-sm-4 fw-bold">Date:</div>
                      <div className="col-sm-8">
                        {moment(data?.date).format("DD MMM YYYY")}
                      </div>
                    </div>
                    <hr />

                    {/* Row 2 */}
                    <div className="row mb-3">
                      <div className="col-sm-4 fw-bold">Amount:</div>
                      <div className="col-sm-8">
                        {number.fNumberDoublePoint(data?.amount)} TZS
                      </div>
                    </div>

                    <hr />

                    {/* Row 3 */}
                    <div className="row mb-3">
                      <div className="col-sm-4 fw-bold">Reference:</div>
                      <div className="col-sm-8">
                        {char.toUpperCase(data?.reference)}
                      </div>
                    </div>

                    <hr />

                    {/* Row 4 */}
                    <div className="row mb-3">
                      <div className="col-sm-4 fw-bold">Control Number:</div>
                      <div className="col-sm-8">{data?.control_number}</div>
                    </div>

                    <hr />

                    {/* Row 5 */}
                    <div className="row mb-3">
                      <div className="col-sm-4 fw-bold">Reason:</div>
                      <div className="col-sm-8">
                        {char.smartTitleCase(data?.reason)}
                      </div>
                    </div>

                    <hr />

                    {/* Row 6 */}
                    <div className="row mb-3">
                      <div className="col-sm-4 fw-bold">Status:</div>
                      <div className="col-sm-8">
                        {data?.is_approved ? (
                          <span className="badge bg-success">Approved</span>
                        ) : (
                          <span className="badge bg-danger">Pending</span>
                        )}
                      </div>
                    </div>

                    <hr />
                    {!data?.is_approved && (
                      <div className="d-flex justify-content-end">
                        <button
                          onClick={() => handleConfirm("1")}
                          className="btn btn-soft-danger me-2"
                        >
                          <i className="bi bi-x-circle me-2"></i>
                          Reject
                        </button>
                        <button
                          onClick={() => handleConfirm("2")}
                          className="btn btn-soft-success"
                        >
                          <i className="bi bi-check-circle me-2"></i>
                          Approve
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Divider />
        <Modal.Footer>
          <div className="container">
            <div className="row">
              <div className="col-md-6 col-sm-12 d-flex justify-content-start"></div>
              <div className="col-md-6 col-sm-12 d-flex justify-content-end">
                <button
                  onClick={() => {
                    setOpen(false);
                    setData(null);
                    setComment("");
                    setError("");
                  }}
                  disabled={loading}
                  type="button"
                  className="btn btn-soft-danger btn-label waves-effect waves-light rounded-pill me-3"
                >
                  <i className="bi bi-x-circle label-icon align-middle rounded-pill fs-16 me-2"></i>{" "}
                  Close
                </button>
              </div>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      {showApproveModal &&
        createPortal(
          <>
            <div
              className="custom-backdrop"
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.3)",
                backdropFilter: "blur(5px)",
                zIndex: 2000,
              }}
            />

            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 2010,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div className="card shadow-lg border-0" style={{ width: 450 }}>
                <div className="card-header bg-success text-white">
                  <h5 className="mb-0">Approval Comment Required</h5>
                </div>

                <div className="card-body">
                  <label className="form-label fw-bold">
                    Comment <span className="text-danger">*</span>
                  </label>

                  <textarea
                    autoFocus
                    className={`form-control ${error ? "is-invalid" : ""}`}
                    rows="4"
                    value={comment}
                    onChange={(e) => {
                      setComment(e.target.value);
                      setError("");
                    }}
                    placeholder="Enter approval comment..."
                  />

                  {error && (
                    <div className="invalid-feedback d-block">{error}</div>
                  )}
                </div>

                <div className="card-footer d-flex justify-content-end">
                  <button
                    className="btn btn-danger me-2"
                    disabled={loading}
                    onClick={() => setShowApproveModal(false)}
                  >
                    <i className="bi bi-x-circle me-1" />
                    Cancel
                  </button>

                  <button
                    className="btn btn-success"
                    disabled={loading}
                    onClick={handleSubmitApprove}
                  >
                    {loading && (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    )}
                    <i className="bi bi-check-circle me-1" /> Confirm Approve
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
      {showRejectModal &&
        createPortal(
          <>
            <div
              className="custom-backdrop"
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.3)",
                backdropFilter: "blur(5px)",
                zIndex: 2000,
              }}
            />

            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 2010,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div className="card shadow-lg border-0" style={{ width: 450 }}>
                <div className="card-header bg-danger text-white">
                  <h5 className="mb-0">Rejection Comment Required</h5>
                </div>

                <div className="card-body">
                  <label className="form-label fw-bold">
                    Comment <span className="text-danger">*</span>
                  </label>

                  <textarea
                    autoFocus
                    className={`form-control ${error ? "is-invalid" : ""}`}
                    rows="4"
                    value={comment}
                    onChange={(e) => {
                      setComment(e.target.value);
                      setError("");
                    }}
                    placeholder="Enter rejection comment..."
                  />

                  {error && (
                    <div className="invalid-feedback d-block">{error}</div>
                  )}
                </div>

                <div className="card-footer d-flex justify-content-end">
                  <button
                    className="btn btn-danger me-2"
                    disabled={loading}
                    onClick={() => setShowRejectModal(false)}
                  >
                    <i className="bi bi-x-circle me-1" />
                    Cancel
                  </button>

                  <button
                    className="btn btn-success"
                    disabled={loading}
                    onClick={handleSubmitReject}
                  >
                    {loading && (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    )}
                    <i className="bi bi-check-circle me-1" /> Confirm Reject
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
};
